import { FunctionComponent, useCallback, useEffect, useState } from 'react'
import useStyles from './styles'
import { useHistory } from 'react-router'
import { toast, Text, getTheme } from '@conexasaude/conexa-hero'
import CustomLayout from '@/components/RebrandedCustomLayout'
import { setCache } from '@/utils/cache'
import { useAttendanceContext } from '@/modules/Attendance/contexts/AttendanceContex'
import { useAuthContext } from '@/modules/Auth/contexts/AuthContext'
import { useFirebaseContext } from '@/modules/Firebase/contexts/FirebaseContext'
import { useParams } from 'react-router-dom'
import { useModalContext } from '@/components/RebrandedModal/contexts/ModalContext'
import ConfirmationProfessionalReservation from '../../components/ConfirmationProfessionalReservation'
import ConfirmationReservationDetails from '../../components/ConfirmationReservationDetails'
import { convertStringIntoDateArray } from '@/utils/date'
import { verifyFreeAttendances } from '@/modules/Attendance/services'
import {
  confirmReservationConsultation,
  recuseReservationConsultation,
  getConsultationDetails,
} from '../../my-calls-services'
import Attendance from '@/modules/Attendance/classes/Attendance'
import { useMedicalSpecialitiesContext } from '@/modules/MedicalSpecialities/contexts/MedicalSpecialitiesContext'
import CancelledReservationModal from '@/modules/Attendance/submodules/Immediate/components/modals/CancelledReservationModal'
import { useReservationConsultationContext } from '../../contexts/consultationReservationContext'
import { useReservationsConsultations } from '../../queries'
import Icon from '@/components/RebrandedHeroIcons'
import { redirectToCheckout } from '@/modules/Checkout/utils/redirectToCheckout'
import { RenderIf } from '@/components/RenderIf'
import { HeroWrapper } from '@/styles/rebranded/HeroWrapper'

const ReservationConfirmation: FunctionComponent = () => {
  const styles = useStyles()
  const { clinic, user } = useAuthContext()
  const [isLoading, setIsLoading] = useState(false)
  const [reservationCancelled, setReservationCancelled] = useState(null)
  const [reservationDetails, setReservationDetails] = useState(null)
  const { logAnalyticsEvent } = useFirebaseContext()
  const { showModal } = useModalContext()
  const { id } = useParams<{ id: string }>()
  const { specialities } = useMedicalSpecialitiesContext()
  const { setAttendance } = useAttendanceContext()
  const { setReservationConsultationData } = useReservationConsultationContext()
  const reservationConsultations = useReservationsConsultations()
  const reservationConsultation = reservationConsultations?.data?.find(
    reservation => {
      return reservation?.idAtendimento?.toString() === id
    },
  )
  const theme = getTheme()

  useEffect(() => {
    getReservationDetails()
    // eslint-disable-next-line
  }, [id])

  const getReservationDetails = useCallback(async () => {
    await getConsultationDetails(id)
      .then(response => {
        setReservationDetails(response)
      })
      .catch(error => {
        const errorMessage =
          error?.response?.data?.msg || error?.response?.msg || error?.message

        toast({
          message: errorMessage,
          variant: 'error',
          autoCloseDuration: 5000,
        })
      })
  }, [id])

  useEffect(() => {
    if (reservationConsultations?.data) {
      const reservationConsult = reservationConsultations?.data?.find(
        reservation => {
          return reservation?.idAtendimento?.toString() === id
        },
      )
      if (!reservationConsult) {
        setReservationCancelled(true)
      } else {
        setReservationCancelled(false)
      }
    }
  }, [id, reservationConsultations?.data])

  const goHome = () => {
    history.push(`/`)
  }

  const goToScheduleProfessional = () => {
    history.push(
      `/atendimento-agendado/categoria/psicologoViva/${reservationDetails?.professional?.id}`,
    )
  }

  const history = useHistory()

  const handleConfirmClick = async () => {
    setIsLoading(true)
    const dateTratative = reservationConsultation?.dataAtendimentoFmt?.replace(
      /\//g,
      '-',
    )
    const hasFreeAttendances = await verifyFreeAttendances({
      date: dateTratative.replace(/\s\d{2}:\d{2}$/, ''),
      preferenceType: 'from_speciality',
      professionalType: 'PSICOLOGO',
      attendanceType: 'AGENDADO',
      specialityId: reservationConsultation?.idProfissional,
      rescheduleToken: reservationConsultation?.token,
    })
    const speciality = specialities.find(s => s.id === 'PSICOLOGO')
    setAttendance(() => {
      const att = new Attendance(speciality)
      att.hour = reservationConsultation?.dataAtendimentoFmt
      att.patientContact = { email: user?.email, cellphone: user?.cellphone }
      att.scheduleId = reservationConsultation?.idAtendimento
      return att
    })
    setCache('has-free-attendance', hasFreeAttendances)

    const hasCheckoutEnabled = clinic?.flag?.PermiteConsultaParticular
    if (hasCheckoutEnabled && hasFreeAttendances === 'limitReached') {
      toast({
        message:
          'Realize o pagamento da consulta. Você atingiu  o limite de atendimentos gratuitos. Finalize esse agendamento, realizando o pagamento.',
        variant: 'warning',
        autoCloseDuration: 5000,
      })
      setReservationConsultationData({
        ...reservationConsultation,
      })
      setTimeout(() => {
        redirectToCheckout({
          attendanceType: 'scheduled',
          professionalType: 'PSICOLOGO',
          push: history.push,
          hasPayment: clinic?.flag?.PermiteConsultaParticular,
          hasMultipleCards: clinic?.flag?.HabilitarMultiplosCartoesConvenio,
        })
      }, 1000)
      return
    }
    if (hasCheckoutEnabled && hasFreeAttendances !== 'limitNotReached') {
      setReservationConsultationData({
        ...reservationConsultation,
      })
      setTimeout(() => {
        redirectToCheckout({
          attendanceType: 'scheduled',
          professionalType: 'PSICOLOGO',
          push: history.push,
          hasPayment: clinic?.flag?.PermiteConsultaParticular,
          hasMultipleCards: clinic?.flag?.HabilitarMultiplosCartoesConvenio,
        })
      }, 1000)
      return
    }
    try {
      await confirmReservationConsultation({
        professionalId: reservationConsultation?.idProfissional,
        id: reservationConsultation?.idAtendimento,
        appointmentDate: reservationConsultation?.dataAtendimentoFmt,
        email: user?.email,
        phone: user?.cellphone,
      })
      setReservationConsultationData({
        ...reservationConsultation,
      })
      history.push(`/checkout/reserva-confirmada`)
      logAnalyticsEvent('Pac_Reserva_ConfirmarReserva')
    } catch (error) {
      const errorMessage =
        error?.response?.data?.msg || error?.response?.msg || error?.message

      toast({
        message: errorMessage,
        variant: 'error',
        autoCloseDuration: 5000,
      })
    }
    setIsLoading(false)
  }

  const handleRecuseClick = async () => {
    try {
      await recuseReservationConsultation(id)
      showModal('CANCELLED_RESERVATION', {
        title: 'Reserva recusada',
        persistent: true,
        width: 512,
      })
      logAnalyticsEvent('Pac_Reserva_Recusar')
      setReservationConsultationData(null)
    } catch (error) {
      const errorMessage =
        error?.response?.data?.msg || error?.response?.msg || error?.message

      toast({
        message: errorMessage,
        variant: 'error',
        autoCloseDuration: 5000,
      })
    }
  }

  return (
    <HeroWrapper>
      <div>
        <RenderIf condition={reservationCancelled}>
          <CustomLayout
            hasPrimaryAction
            hasNegativeAction
            primaryActionLabel="Agendar novo horário"
            negativeActionLabel="Agora não"
            negativeAction={goHome}
            primaryAction={() => goToScheduleProfessional()}
            primaryActionProps={{
              disabled: isLoading || !reservationDetails,
              busy: isLoading,
              id: 'ReservaCancelada-Clique_agendar_novo_horario',
            }}
          >
            <div css={styles.reservationCancelledWrapper}>
              <div css={styles.reservationCancelledIllustration}>
                <Icon name="reservation-cancelled" width="230px" />
              </div>
              <Text variant="headingLarge">
                Sua reserva de consulta foi cancelada pelo profissional
              </Text>
              <Text renderAs="span">Seu horário reservado foi cancelado.</Text>
            </div>
          </CustomLayout>
        </RenderIf>
        <RenderIf
          condition={!reservationCancelled && !!reservationConsultation}
        >
          <CustomLayout
            hasPrimaryAction
            hasNegativeAction
            primaryActionLabel="Confirmar reserva"
            negativeActionLabel="Recusar"
            negativeAction={handleRecuseClick}
            primaryAction={() => handleConfirmClick()}
            primaryActionProps={{
              disabled: isLoading,
              busy: isLoading,
              id: 'AtendimentoAgendado-ConfirmarReserva-Clique_confirma',
            }}
          >
            <div>
              <Text variant="headingLarge" css={styles.confirmTitle}>
                Confirme sua reserva
              </Text>
              <Text>
                Confira os detalhes do horário reservado para sua consulta
              </Text>
              <ConfirmationProfessionalReservation
                photo={reservationConsultation?.urlFoto}
                professionalName={reservationConsultation?.nomeProfissional}
                professionalSpeciality={reservationConsultation?.especialidade}
                description={reservationConsultation?.modoAtendimento}
              />
              <ConfirmationReservationDetails
                date={convertStringIntoDateArray(
                  reservationConsultation?.dataAtendimentoFmt?.toString() ||
                    '00/00/0000 00:00',
                )}
              />
            </div>
            <div css={styles.containerText}>
              <Icon name="info" color={theme.colors.neutral[300]} />
              <Text>
                Você pode reagendar ou cancelar a consulta com até{' '}
                {clinic?.minimumHoursToCancel
                  ? clinic?.minimumHoursToCancel
                  : '24'}
                h de antecedência.
              </Text>
            </div>
            <CancelledReservationModal />
          </CustomLayout>
        </RenderIf>
      </div>
    </HeroWrapper>
  )
}

export default ReservationConfirmation
